import { Lock, Maximize2, MonitorPlay } from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useParams } from "react-router-dom";
import {
	DATE_RANGE_PRESETS,
	NoteContent,
	ReportRows,
	TvHeader,
	WidgetChart,
	WidgetMaximizeDialog,
	effectiveGrain,
	formatRangeShort,
	presetLabel,
	legacyOrder,
	resetToRows,
	resolveDateRange,
	useReportFit,
	useTvCondensed,
	useTvFullscreen,
} from "../../components/Shared/ReportBuilder";
import { resolveCompare } from "../../components/Shared/ReportBuilder/WidgetCard";
import { Skeleton } from "../../components/ui/skeleton";
import {
	ComparisonMode,
	FILTER_DIMENSIONS,
	GRAIN_LABELS,
	PublicDashboard,
	isNote,
	widgetTitle,
} from "../../models/ReportBuilder";
import {
	PUBLIC_REFRESH_MS,
	usePublicReport,
	usePublicWidgetData,
} from "../../networking/report-builder.hooks";

const pinKey = (shareId: string) => `report-pin:${shareId}`;

/**
 * Public, login-free view of a shared report (for a TV or a stakeholder).
 * Asks for the PIN when the share has one and refreshes itself every five
 * minutes. Widget queries run server-side by widget id; no SQL here.
 */
const PublicReportPage: React.FC = () => {
	const { shareId = "" } = useParams<{ shareId: string }>();
	const [pin, setPin] = useState<string | undefined>(
		() => sessionStorage.getItem(pinKey(shareId)) ?? undefined
	);
	const [pinInput, setPinInput] = useState("");
	const report = usePublicReport(shareId, pin);
	const [tv, setTv] = useState(false);
	useTvFullscreen(tv, setTv);
	const [condensed, setCondensed] = useTvCondensed();

	const code = report.error?.error?.code;
	const needsPin = code === "REPORT_PIN_REQUIRED" || code === "REPORT_PIN_INVALID";

	const submitPin = (e: React.FormEvent) => {
		e.preventDefault();
		sessionStorage.setItem(pinKey(shareId), pinInput.trim());
		setPin(pinInput.trim());
	};

	if (needsPin) {
		return (
			<Shell>
				<form
					onSubmit={submitPin}
					className="mx-auto mt-24 flex w-80 flex-col items-center gap-3 rounded-lg border bg-white p-6 shadow-sm"
				>
					<Lock className="h-6 w-6 text-gray-500" />
					<p className="text-sm text-gray-700">Den här dashboarden kräver en PIN-kod.</p>
					<input
						autoFocus
						value={pinInput}
						onChange={(e) => setPinInput(e.target.value)}
						inputMode="numeric"
						className="w-full rounded-md border border-gray-300 px-3 py-2 text-center text-lg tracking-widest"
					/>
					{code === "REPORT_PIN_INVALID" && pin && (
						<span className="text-xs text-red-600">Fel PIN.</span>
					)}
					<button
						type="submit"
						className="w-full rounded-md bg-brand px-3 py-2 text-sm font-medium text-white"
					>
						Öppna
					</button>
				</form>
			</Shell>
		);
	}

	if (report.error) {
		return (
			<Shell>
				<p className="mt-24 text-center text-sm text-gray-600">
					{code === "REPORT_SHARE_NOT_FOUND"
						? "Den här dashboarden delas inte längre."
						: report.error.error?.detail || "Dashboarden kunde inte hämtas."}
				</p>
			</Shell>
		);
	}

	if (!report.data) {
		return (
			<Shell>
				<Skeleton className="mt-6 h-64 w-full" />
			</Shell>
		);
	}

	return (
		<Shell tv={tv} condensed={condensed}>
			<PublicDashboardView
				dashboard={report.data}
				pin={pin}
				fetchedAt={report.dataUpdatedAt}
				tv={tv}
				setTv={setTv}
				condensed={condensed}
				setCondensed={setCondensed}
			/>
		</Shell>
	);
};

/** The page frame. In TV mode it is exactly the screen and never scrolls; the rows fit inside. */
const Shell: React.FC<{ tv?: boolean; condensed?: boolean; children: React.ReactNode }> = ({
	tv,
	condensed,
	children,
}) => (
	<div
		className={cn(
			"min-h-screen bg-gray-100 p-6",
			tv && "flex h-screen flex-col overflow-hidden",
			tv && condensed && "p-4"
		)}
	>
		{children}
	</div>
);

const PublicDashboardView: React.FC<{
	dashboard: PublicDashboard;
	pin?: string;
	fetchedAt: number;
	tv: boolean;
	setTv: (tv: boolean) => void;
	condensed: boolean;
	setCondensed: (condensed: boolean) => void;
}> = ({ dashboard, pin, fetchedAt, tv, setTv, condensed, setCondensed }) => {
	const range = useMemo(() => resolveDateRange(dashboard.range), [dashboard.range]);
	// Shares saved before rows carry grid positions; they show one widget per row in reading order until re-shared.
	const rows = useMemo(
		() => dashboard.rows ?? resetToRows(legacyOrder(dashboard.widgets).map((w) => w.id)),
		[dashboard.rows, dashboard.widgets]
	);
	const rangeLabel =
		dashboard.range.preset === "custom"
			? `${range.from} – ${range.to}`
			: DATE_RANGE_PRESETS.find((p) => p.value === dashboard.range.preset)?.label ?? "";
	const activeFilters = FILTER_DIMENSIONS.filter(
		(d) => (dashboard.filters?.[d.key]?.length ?? 0) > 0
	)
		.map((d) => `${d.label.toLowerCase()}: ${dashboard.filters![d.key]!.length}`)
		.join(", ");
	const meta = (
		<>
			{rangeLabel}
			{activeFilters ? ` · filtrerad (${activeFilters})` : ""} · Uppdaterad{" "}
			{new Date(fetchedAt || Date.now()).toLocaleTimeString("sv-SE", {
				hour: "2-digit",
				minute: "2-digit",
			})}{" "}
			· uppdateras var {Math.round(PUBLIC_REFRESH_MS / 60000)}:e minut
		</>
	);
	return (
		<>
			{tv ? (
				<div className={condensed ? "mb-3" : "mb-4"}>
					<TvHeader
						name={dashboard.name}
						meta={meta}
						condensed={condensed}
						onCondensed={setCondensed}
						onExit={() => setTv(false)}
					/>
				</div>
			) : (
				<div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
					<h1 className="text-2xl font-semibold text-gray-900">{dashboard.name}</h1>
					<div className="text-sm text-gray-500">{meta}</div>
					<button
						type="button"
						onClick={() => setTv(true)}
						className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
						title="Helskärm som fyller skärmen utan att scrolla"
					>
						<MonitorPlay className="h-3.5 w-3.5" /> TV-läge
					</button>
				</div>
			)}
			<ReportRows
				rows={rows}
				widgets={dashboard.widgets}
				editable={false}
				fit={tv}
				condensed={condensed}
				renderWidget={(id) => {
					const widget = dashboard.widgets.find((w) => w.id === id)!;
					const own = widget.spec.period ? resolveDateRange({ preset: widget.spec.period }) : range;
					return (
						<PublicWidgetCard
							shareId={dashboard.shareId}
							widget={widget}
							range={own}
							compare={dashboard.compare}
							pin={pin}
						/>
					);
				}}
			/>
		</>
	);
};

const PublicWidgetCard: React.FC<{
	shareId: string;
	widget: PublicDashboard["widgets"][number];
	range: { from: string; to: string };
	compare?: ComparisonMode;
	pin?: string;
}> = ({ shareId, widget, range, compare, pin }) => {
	const note = isNote(widget);
	const bucket = widget.spec.bucket ?? "auto";
	const grain = effectiveGrain(bucket, range);
	const effectiveCompare = widget.spec.usesDateRange
		? resolveCompare(widget.display, compare)
		: undefined;
	const { data, isLoading, error } = usePublicWidgetData(
		shareId,
		widget.id,
		range,
		effectiveCompare,
		pin,
		bucket,
		!note
	);
	const [maximized, setMaximized] = useState(false);
	const { title, description } = widgetTitle(widget);
	/** Condensed TV mode: a number tile puts its title beside the number. */
	const condensedTile = useReportFit().condensed && !note && widget.spec.chart.type === "number";
	if (note) {
		return (
			<div className="h-full overflow-auto">
				<NoteContent text={widget.note ?? ""} />
			</div>
		);
	}
	const periodLabel = `${widget.spec.period ? presetLabel(widget.spec.period) : rangeText(range)}${
		widget.spec.usesBucket ? ` · per ${GRAIN_LABELS[grain].toLowerCase()}` : ""
	}`;
	return (
		<div
			className={cn(
				"flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm",
				condensedTile && "flex-row items-center gap-4 py-3"
			)}
		>
			<div
				className={cn(
					"mb-2 flex items-start justify-between gap-2",
					condensedTile && "mb-0 min-w-0 flex-1"
				)}
			>
				<div className="min-w-0">
					<h3 className="truncate text-sm font-semibold text-gray-900">{title}</h3>
					{description && <p className="truncate text-xs text-gray-500">{description}</p>}
					{widget.spec.period && (
						<p className="text-xs text-action">{presetLabel(widget.spec.period)}</p>
					)}
				</div>
				{!condensedTile && (
					<button
						type="button"
						onClick={() => setMaximized(true)}
						className="rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-700"
						aria-label="Maximera"
						title="Maximera"
					>
						<Maximize2 className="h-3.5 w-3.5" />
					</button>
				)}
			</div>
			<div className={cn("min-h-0 flex-1", condensedTile && "flex-none")}>
				{isLoading && <Skeleton className={cn("h-full w-full", condensedTile && "h-10 w-32")} />}
				{error && (
					<div className="rounded bg-red-50 p-3 text-xs text-red-700">
						{error.error?.detail || "Kunde inte hämta data"}
					</div>
				)}
				{data && !error && (
					<WidgetChart
						spec={widget.spec}
						data={data}
						display={widget.display}
						height="100%"
						layout={condensedTile ? "row" : "tile"}
						onPointClick={() => setMaximized(true)}
					/>
				)}
			</div>
			<WidgetMaximizeDialog
				open={maximized}
				onOpenChange={setMaximized}
				widget={widget}
				data={data}
				periodLabel={periodLabel}
				showSql={false}
			/>
		</div>
	);
};

const rangeText = (range: { from: string; to: string }) => formatRangeShort(range);

export default PublicReportPage;
